import { useMemo, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ListChecks,
  Quote,
  RotateCcw,
  Sparkles,
  X,
} from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';
import type { LeadActionType, LeadTaskSuggestion } from '../../../lib/leadsApi';
import {
  useAcceptLeadSuggestion,
  useDismissLeadSuggestion,
  useGenerateLeadSuggestions,
  useLeadSuggestions,
  useRestoreLeadSuggestion,
} from '../../../hooks/useLeads';
import { useUsers } from '../../../hooks/useUsers';
import { PRIORITY_BADGE } from '../../tasks/taskConstants';
import { Select } from '../../ui/Select';
import { DateField } from '../../ui/DateField';
import { formatDate } from '../../../lib/format';

/**
 * „Sugestii de acțiuni” — what Firmio AI proposed for this lead (from a
 * conversation with the assistant, from a note, or on request). Each pending
 * card is a small editable form (title, type, owner, deadline, details) with
 * two decisions: add it to the lead's actions, or dismiss it. Mirrors the
 * task suggestions of a meeting.
 */
export interface LeadTaskSuggestionsProps {
  leadId: string;
  readOnly?: boolean;
}

const ACTION_TYPE_OPTIONS: Array<{ value: LeadActionType; label: string }> = [
  { value: 'CALL', label: 'Apel' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'MEETING', label: 'Întâlnire' },
  { value: 'TASK', label: 'Sarcină' },
  { value: 'FOLLOW_UP', label: 'Follow-up' },
  { value: 'PROPOSAL', label: 'Ofertă' },
  { value: 'DEMO', label: 'Demo' },
  { value: 'OTHER', label: 'Altele' },
];

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
const labelCls = 'mb-1 block text-xs font-medium text-gray-600';

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function LeadTaskSuggestions({ leadId, readOnly = false }: LeadTaskSuggestionsProps) {
  const { t } = useI18n();
  const { data: suggestions = [], isLoading } = useLeadSuggestions(leadId);
  const generateMutation = useGenerateLeadSuggestions(leadId);
  const { data: users } = useUsers();
  const userOptions = useMemo(
    () => (users ?? []).map((u) => ({ value: u.id, label: u.name })),
    [users],
  );

  const pending = suggestions.filter((s) => s.status === 'PENDING');
  const accepted = suggestions.filter((s) => s.status === 'ACCEPTED');
  const dismissed = suggestions.filter((s) => s.status === 'DISMISSED');
  const [showDismissed, setShowDismissed] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  async function handleGenerate() {
    setGenerateError(null);
    try {
      await generateMutation.mutateAsync();
    } catch (err) {
      setGenerateError(errorText(err, t('leads.suggestions.generateFailed')));
    }
  }

  // Nothing proposed and nothing to show: stay out of the way.
  if (!isLoading && suggestions.length === 0 && readOnly) return null;

  return (
    <section className="mt-8">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-[13px] font-semibold text-gray-500">
            <ListChecks size={14} className="text-brand-600" />
            {t('leads.suggestions.title')}
            {pending.length > 0 && (
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                {pending.length}
              </span>
            )}
          </h2>
          <p className="mt-1 text-xs text-gray-500">{t('leads.suggestions.hint')}</p>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={generateMutation.isPending}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Sparkles size={13} className={generateMutation.isPending ? 'animate-pulse' : ''} />
            {generateMutation.isPending
              ? t('leads.suggestions.generating')
              : suggestions.length
                ? t('leads.suggestions.regenerate')
                : t('leads.suggestions.generate')}
          </button>
        )}
      </div>

      {generateError && (
        <p className="mt-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {generateError}
        </p>
      )}

      <div className="mt-3 space-y-3">
        {isLoading ? (
          <p className="text-sm text-gray-400">{t('common.loading')}</p>
        ) : pending.length === 0 ? (
          <p className="rounded-md border border-dashed border-gray-200 px-4 py-4 text-center text-xs text-gray-500">
            {suggestions.length ? t('leads.suggestions.allDecided') : t('leads.suggestions.empty')}
          </p>
        ) : (
          pending.map((s) => (
            <SuggestionCard
              key={s.id}
              leadId={leadId}
              suggestion={s}
              userOptions={userOptions}
              readOnly={readOnly}
            />
          ))
        )}
      </div>

      {accepted.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('leads.suggestions.accepted', { count: accepted.length })}
          </h3>
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
            {accepted.map((s) => (
              <li
                key={s.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
              >
                <Check size={14} className="shrink-0 text-green-600" />
                <span className="font-medium text-gray-800">{s.leadAction?.title ?? s.title}</span>
                <span className="text-xs text-gray-500">
                  {[
                    ACTION_TYPE_OPTIONS.find(
                      (o) => o.value === (s.leadAction?.type ?? s.actionType),
                    )?.label ?? null,
                    s.assignee?.name ?? null,
                    s.decidedBy
                      ? t('leads.suggestions.decidedBy', { name: s.decidedBy.name })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {dismissed.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowDismissed((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700"
          >
            {showDismissed ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {t('leads.suggestions.dismissed', { count: dismissed.length })}
          </button>
          {showDismissed && (
            <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
              {dismissed.map((s) => (
                <DismissedRow key={s.id} leadId={leadId} suggestion={s} readOnly={readOnly} />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

// --- One pending suggestion ---------------------------------------------------------

function SuggestionCard({
  leadId,
  suggestion: s,
  userOptions,
  readOnly,
}: {
  leadId: string;
  suggestion: LeadTaskSuggestion;
  userOptions: { value: string; label: string }[];
  readOnly: boolean;
}) {
  const { t } = useI18n();
  const acceptMutation = useAcceptLeadSuggestion(leadId);
  const dismissMutation = useDismissLeadSuggestion(leadId);

  const [title, setTitle] = useState(s.title);
  const [description, setDescription] = useState(s.description ?? '');
  const [showDescription, setShowDescription] = useState(Boolean(s.description));
  const [type, setType] = useState<LeadActionType>(s.actionType ?? 'FOLLOW_UP');
  const [assigneeId, setAssigneeId] = useState(s.assignee?.id ?? '');
  const [dueDate, setDueDate] = useState(s.dueDate ?? '');
  const [error, setError] = useState<string | null>(null);

  const busy = acceptMutation.isPending || dismissMutation.isPending;
  const canAccept = title.trim().length > 0 && !busy && !readOnly;

  async function handleAccept() {
    setError(null);
    try {
      await acceptMutation.mutateAsync({
        id: s.id,
        input: {
          title: title.trim(),
          description: description.trim() || null,
          type,
          assigneeId: assigneeId || null,
          dueDate: dueDate || null,
        },
      });
    } catch (err) {
      setError(errorText(err, t('leads.suggestions.acceptFailed')));
    }
  }

  async function handleDismiss() {
    setError(null);
    try {
      await dismissMutation.mutateAsync(s.id);
    } catch (err) {
      setError(errorText(err, t('leads.suggestions.dismissFailed')));
    }
  }

  return (
    <article className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
      <div className="flex items-start gap-2">
        {s.priority !== 'NONE' && (
          <span
            className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${PRIORITY_BADGE[s.priority]}`}
          >
            {t(`tasks.priority.${s.priority}`)}
          </span>
        )}
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={readOnly}
          className={`${inputCls} bg-white font-medium`}
          aria-label={t('leads.suggestions.field.title')}
        />
      </div>

      {s.reason && (
        <p className="mt-2 flex items-start gap-1.5 text-xs italic text-gray-600">
          <Quote size={12} className="mt-0.5 shrink-0 text-gray-400" />
          <span>{s.reason}</span>
        </p>
      )}
      {s.assigneeHint && (
        <p className="mt-1 text-xs text-amber-700">
          {t('leads.suggestions.assigneeHint', { name: s.assigneeHint })}
        </p>
      )}

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Select
          label={t('leads.suggestions.field.type')}
          size="sm"
          value={type}
          onChange={(v) => setType(v as LeadActionType)}
          options={ACTION_TYPE_OPTIONS}
          disabled={readOnly}
        />
        <Select
          label={t('leads.suggestions.field.assignee')}
          size="sm"
          searchable
          clearable
          value={assigneeId}
          onChange={setAssigneeId}
          options={userOptions}
          placeholder={t('leads.suggestions.noAssignee')}
          disabled={readOnly}
        />
        <div>
          <label className={labelCls}>{t('leads.suggestions.field.dueDate')}</label>
          <DateField
            value={dueDate}
            onChange={setDueDate}
            disabled={readOnly}
            placeholder={formatDate(null)}
          />
        </div>
      </div>

      <div className="mt-3">
        {showDescription ? (
          <>
            <label className={labelCls}>{t('leads.suggestions.field.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={readOnly}
              className={`${inputCls} bg-white`}
            />
          </>
        ) : (
          !readOnly && (
            <button
              type="button"
              onClick={() => setShowDescription(true)}
              className="text-xs text-brand-700 hover:underline"
            >
              + {t('leads.suggestions.addDescription')}
            </button>
          )
        )}
      </div>

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => void handleDismiss()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-white disabled:opacity-50"
          >
            <X size={14} /> {t('leads.suggestions.dismiss')}
          </button>
          <button
            type="button"
            onClick={() => void handleAccept()}
            disabled={!canAccept}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Check size={14} />
            {acceptMutation.isPending
              ? t('leads.suggestions.accepting')
              : t('leads.suggestions.accept')}
          </button>
        </div>
      )}
    </article>
  );
}

// --- One dismissed suggestion ------------------------------------------------------

function DismissedRow({
  leadId,
  suggestion: s,
  readOnly,
}: {
  leadId: string;
  suggestion: LeadTaskSuggestion;
  readOnly: boolean;
}) {
  const { t } = useI18n();
  const restoreMutation = useRestoreLeadSuggestion(leadId);
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
      <span className="text-gray-500 line-through">{s.title}</span>
      {!readOnly && (
        <button
          type="button"
          onClick={() => restoreMutation.mutate(s.id)}
          disabled={restoreMutation.isPending}
          className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline disabled:opacity-50"
        >
          <RotateCcw size={12} /> {t('leads.suggestions.restore')}
        </button>
      )}
    </li>
  );
}
